import { useI18n } from '@ecom/i18n';
import { Button, Checkbox, Field, Input, Select } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { provisionMerchantFormSchema, type ProvisionMerchantFormValues } from '../schemas';

import { adminName, type AssignableAdmin } from '@/features/portfolios/types';
import { whenDefined } from '@/lib/optional-props';

/**
 * `POST /admin/organisations` - an operator creates a merchant's account and organisation.
 *
 * One real decision on this screen besides the address and the name: `organisationActive` - is this company already vetted (it starts
 * trading at once) or does it wait in the ordinary approval queue. It is a deliberate choice, not a default buried in the API, and its
 * hint says what leaving it unchecked does.
 *
 * The last field is the operator's OWN password, typed again: the platform has no second factor, so a stolen or unattended session must
 * not be enough to create an account.
 *
 * `onSubmit` must REJECT on failure so the values survive a refusal (the same bug `AssignRoleForm` already documents, where an
 * unconditional reset silently cleared a form whose request had failed). The password field is the one exception: it is cleared in
 * every case, success or not, so that it does not linger in the DOM.
 */
export function ProvisionOrganisationForm({
  onSubmit,
  isSubmitting,
  managers,
}: {
  onSubmit: (values: ProvisionMerchantFormValues) => Promise<void>;
  isSubmitting: boolean;
  /**
   * `undefined` for an administrator: the new merchant joins ITS portfolio, the form says so and offers no choice. A list (possibly
   * still empty while it loads) for a superadmin, who is not a manager and must choose the administrator who will manage and approve.
   */
  managers?: readonly AssignableAdmin[] | undefined;
}): ReactElement {
  const { t, locale: screenLocale } = useI18n();
  const {
    register,
    control,
    handleSubmit,
    reset,
    resetField,
    setError,
    formState: { errors },
  } = useForm<ProvisionMerchantFormValues>({
    resolver: zodResolver(provisionMerchantFormSchema),
    defaultValues: {
      email: '',
      legalName: '',
      organisationActive: true,
      managedBy: '',
      locale: screenLocale,
      reason: '',
      confirmPassword: '',
    },
  });
  const mustChooseManager = managers !== undefined;

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(async (values) => {
          if (mustChooseManager && (values.managedBy === undefined || values.managedBy === '')) {
            setError('managedBy', { message: t('admin.organisations.managerRequired') });
            resetField('confirmPassword');
            return;
          }
          try {
            await onSubmit(values);
            reset();
          } catch {
            // Surfaced by the parent as a toast - the values stay so the operator does not retype them...
            resetField('confirmPassword');
          }
        })(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <Field
        label={t('admin.organisations.emailLabel')}
        {...whenDefined('error', errors.email?.message)}
      >
        {(bind) => <Input {...bind} {...register('email')} type="email" autoComplete="off" />}
      </Field>

      <Field
        label={t('admin.organisations.legalNameLabel')}
        {...whenDefined('error', errors.legalName?.message)}
      >
        {(bind) => <Input {...bind} {...register('legalName')} type="text" autoComplete="off" />}
      </Field>

      {mustChooseManager ? (
        <Field
          label={t('admin.organisations.managerLabel')}
          hint={t('admin.organisations.managerHint')}
          {...whenDefined('error', errors.managedBy?.message)}
        >
          {(bind) => (
            <Select
              {...bind}
              {...register('managedBy')}
              placeholder={t('admin.assign.adminPlaceholder')}
              options={managers.map((admin) => ({
                value: admin.userId,
                label: `${adminName(admin)} — ${admin.email}`,
              }))}
            />
          )}
        </Field>
      ) : (
        <p role="note" className="rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
          {t('admin.organisations.ownPortfolioNote')}
        </p>
      )}

      <Field
        label={t('admin.organisations.localeLabel')}
        hint={t('admin.organisations.localeHint')}
      >
        {(bind) => (
          <Select
            {...bind}
            {...register('locale')}
            options={[
              { value: 'fr', label: 'Français' },
              { value: 'ar', label: 'العربية' },
              { value: 'en', label: 'English' },
            ]}
          />
        )}
      </Field>

      <Controller
        control={control}
        name="organisationActive"
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <Checkbox
              label={t('admin.organisations.organisationActiveLabel')}
              checked={field.value}
              onChange={field.onChange}
            />
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('admin.organisations.organisationActiveHint')}
            </p>
          </div>
        )}
      />

      <Field
        label={t('admin.organisations.reasonLabel')}
        {...whenDefined('error', errors.reason?.message)}
      >
        {(bind) => <Input {...bind} {...register('reason')} type="text" autoComplete="off" />}
      </Field>

      <Field
        label={t('admin.common.confirmPasswordLabel')}
        hint={t('admin.common.confirmPasswordHint')}
        {...whenDefined('error', errors.confirmPassword?.message)}
      >
        {(bind) => (
          <Input
            {...bind}
            {...register('confirmPassword')}
            type="password"
            // 'new-password', not 'current-password': a re-confirmation is the person typing their own password again, and the
            // browser would otherwise fill in whatever it saved for this host (every local console shares 'localhost', so
            // it could be another account's password, and the server refused the action).
            autoComplete="new-password"
          />
        )}
      </Field>

      <Button type="submit" disabled={isSubmitting}>
        {t('admin.organisations.submit')}
      </Button>
    </form>
  );
}
